// PetCharacters.js — Premium cartoon characters
// Design rules: 60% big head, huge eyes, chibi proportions, expressive faces
import React, { useState, useEffect, useRef } from 'react';

const IDLE_ANIMS = ['float','wave','look','spin','shiver','bounce','float','float'];

export function useIdleAnim() {
  const [anim, setAnim] = useState('float');
  useEffect(() => {
    const id = setInterval(() => {
      setAnim(IDLE_ANIMS[Math.floor(Math.random() * IDLE_ANIMS.length)]);
    }, 3500 + Math.random() * 3000);
    return () => clearInterval(id);
  }, []);
  return anim;
}

function useBlinkTimer() {
  const [closed, setClosed] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const tick = () => {
      ref.current = setTimeout(() => {
        setClosed(true);
        setTimeout(() => { setClosed(false); tick(); }, 130);
      }, 2200 + Math.random() * 2500);
    };
    tick();
    return () => clearTimeout(ref.current);
  }, []);
  return closed;
}

function getBodyAnim(idleAnim, mood, tapped) {
  if (tapped)              return 'petBounce 0.6s cubic-bezier(.36,1.2,.58,1)';
  if (mood==='happy')      return 'petBounce 0.9s cubic-bezier(.36,1.1,.58,1) infinite';
  if (idleAnim==='bounce') return 'petBounce 0.85s cubic-bezier(.36,1.1,.58,1)';
  if (idleAnim==='spin')   return 'petSpin 0.7s ease-in-out';
  if (idleAnim==='shiver') return 'petShiver 0.5s ease-in-out';
  return 'petFloat 2.8s ease-in-out infinite';
}

// ─── LUMIE — Chibi Star Girl ────────────────────────────────
// Big round golden face, star-tipped ears, sparkle eyes, tiny round body
// ── TALKING MOUTH HOOK ───────────────────────────────────────
function useTalkingMouth(talking) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!talking) { setOpen(false); return; }
    // Rapid open/close when speaking — 120ms cycle
    const id = setInterval(() => setOpen(o => !o), 120);
    return () => clearInterval(id);
  }, [talking]);
  return open;
}

export function LumiePet({ mood, stage, size=160, idleAnim='float', tapped, talking=false }) {
  const blink = useBlinkTimer();
  const mouthOpen = useTalkingMouth(talking);
  const happy = mood==='happy'||mood==='loved';
  const sad   = mood==='sad';
  const w = size, h = size;
  const bodyAnim = getBodyAnim(idleAnim, mood, tapped);
  const waveArm  = (mood==='happy'||idleAnim==='wave') ? 'petWaveArm 0.5s ease-in-out infinite' : 'none';

  return (
    <div style={{ position:'relative', width:w, height:h, display:'inline-block' }}>
      {/* Outer glow */}
      <div style={{ position:'absolute', inset:-16, borderRadius:'50%',
        background:'radial-gradient(circle, #FFD60040 0%, transparent 70%)',
        animation:'petGlow 2.5s ease-in-out infinite', pointerEvents:'none' }}/>

      <svg viewBox="0 0 200 220" width={w} height={h} style={{ overflow:'visible' }}>
        <defs>
          {/* Face gradient — warm golden 3D sphere */}
          <radialGradient id="lf" cx="38%" cy="28%" r="62%">
            <stop offset="0%"   stopColor="#FFFDE0"/>
            <stop offset="30%"  stopColor="#FFE566"/>
            <stop offset="70%"  stopColor="#FFD600"/>
            <stop offset="100%" stopColor="#E6A800"/>
          </radialGradient>
          {/* Body gradient */}
          <radialGradient id="lb" cx="38%" cy="28%" r="62%">
            <stop offset="0%"   stopColor="#FFE566"/>
            <stop offset="100%" stopColor="#FFAA00"/>
          </radialGradient>
          {/* Specular */}
          <radialGradient id="ls" cx="32%" cy="22%" r="40%">
            <stop offset="0%" stopColor="white" stopOpacity="0.55"/>
            <stop offset="100%" stopColor="white" stopOpacity="0"/>
          </radialGradient>
          <filter id="lglow">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="b"/>
            <feColorMatrix in="b" values="1 0.8 0 0 0 0.8 0.6 0 0 0 0 0 0 0 0 0 0 0 0.55 0" result="g"/>
            <feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Stage 5 legendary halo */}
        {stage?.level>=5 && (
          <ellipse cx="100" cy="72" rx="78" ry="18" fill="none"
            stroke="#FFD600" strokeWidth="4" strokeDasharray="10 6" opacity="0.6"
            style={{ animation:'petSpin 5s linear infinite', transformOrigin:'100px 72px' }}/>
        )}

        {/* === STAR EARS (signature feature) === */}
        {/* Left ear — 5-point star */}
        <polygon points="32,32 36,48 22,40 42,40 28,48"
          fill="#FFD600" stroke="#E6A800" strokeWidth="1.5"
          style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}/>
        {/* Right ear */}
        <polygon points="168,32 172,48 158,40 178,40 164,48"
          fill="#FFD600" stroke="#E6A800" strokeWidth="1.5"
          style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}/>

        {/* Crown stage 3+ */}
        {stage?.level>=3 && (
          <g transform="translate(68,4)">
            <polygon points="0,22 11,2 22,14 33,2 44,22 44,28 0,28"
              fill="#FFD600" stroke="#E6A800" strokeWidth="1.5"
              style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}/>
            <circle cx="11" cy="10" r="5" fill="#FF2D55"/>
            <circle cx="22" cy="14" r="5" fill="#00C851"/>
            <circle cx="33" cy="10" r="5" fill="#4488FF"/>
          </g>
        )}

        {/* === BIG ROUND HEAD (60% of image) === */}
        <g style={{ transformOrigin:'100px 88px', animation:bodyAnim }} filter="url(#lglow)">

          {/* Head shadow */}
          <ellipse cx="100" cy="92" rx="72" ry="70" fill="#E6A800" opacity="0.25"/>
          {/* Head */}
          <ellipse cx="100" cy="88" rx="72" ry="70" fill="url(#lf)"/>
          {/* Shine */}
          <ellipse cx="100" cy="88" rx="72" ry="70" fill="url(#ls)"/>

          {/* Forehead star mark (signature) */}
          <text x="100" y="48" textAnchor="middle" fontSize="14" fill="#FFAA00" opacity="0.7">✦</text>

          {/* === EYES — huge expressive === */}
          <g style={{ animation: idleAnim==='look'?'petLook 1.2s ease-in-out':'none' }}>
            {blink ? (
              <>
                <path d="M62 84 Q72 78 82 84" stroke="#1A1A2E" strokeWidth="4" fill="none" strokeLinecap="round"/>
                <path d="M118 84 Q128 78 138 84" stroke="#1A1A2E" strokeWidth="4" fill="none" strokeLinecap="round"/>
              </>
            ) : (
              <>
                {/* Left eye */}
                <ellipse cx="72" cy="86" rx="17" ry="19" fill="white" style={{ filter:'drop-shadow(0 1px 3px rgba(0,0,0,0.15))' }}/>
                <ellipse cx="72" cy="88" rx="11" ry="12" fill={happy?'#1A6B1A':sad?'#4A3060':'#1A1A6E'}/>
                <ellipse cx="72" cy="90" rx="7"  ry="8"  fill="#0A0A1A"/>
                <circle  cx="77" cy="82" r="5"   fill="white"/>
                <circle  cx="68" cy="88" r="2.5" fill="rgba(255,255,255,0.6)"/>
                {happy && <text x="72" y="94" textAnchor="middle" fontSize="10" fill="white">★</text>}
                {/* Right eye */}
                <ellipse cx="128" cy="86" rx="17" ry="19" fill="white" style={{ filter:'drop-shadow(0 1px 3px rgba(0,0,0,0.15))' }}/>
                <ellipse cx="128" cy="88" rx="11" ry="12" fill={happy?'#1A6B1A':sad?'#4A3060':'#1A1A6E'}/>
                <ellipse cx="128" cy="90" rx="7"  ry="8"  fill="#0A0A1A"/>
                <circle  cx="133" cy="82" r="5"   fill="white"/>
                <circle  cx="124" cy="88" r="2.5" fill="rgba(255,255,255,0.6)"/>
                {happy && <text x="128" y="94" textAnchor="middle" fontSize="10" fill="white">★</text>}
              </>
            )}
          </g>

          {/* Rosy cheeks */}
          <ellipse cx="46"  cy="106" rx="16" ry="11" fill="#FF8888" opacity={happy?0.65:0.35}/>
          <ellipse cx="154" cy="106" rx="16" ry="11" fill="#FF8888" opacity={happy?0.65:0.35}/>

          {/* Mouth — animates when talking */}
          {talking && mouthOpen
            ? <ellipse cx="100" cy="122" rx="18" ry="10" fill="#1A1A2E"/>
            : talking && !mouthOpen
              ? <path d="M82 122 Q100 130 118 122" stroke="#1A1A2E" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              : sad
                ? <path d="M72 120 Q100 110 128 120" stroke="#1A1A2E" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
                : happy
                  ? <path d="M70 116 Q100 138 130 116" stroke="#1A1A2E" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
                  : <path d="M75 118 Q100 128 125 118" stroke="#1A1A2E" strokeWidth="3"   fill="none" strokeLinecap="round"/>
          }

          {/* === TINY ROUND BODY (chibi) === */}
          <ellipse cx="100" cy="174" rx="38" ry="34" fill="url(#lb)"
            style={{ filter:'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' }}/>
          <ellipse cx="100" cy="174" rx="38" ry="34" fill="url(#ls)"/>

          {/* LEFT ARM */}
          <g style={{ transformOrigin:'58px 168px', animation:waveArm }}>
            <ellipse cx="56" cy="170" rx="12" ry="18" fill="#FFD600"
              style={{ filter:'drop-shadow(-2px 2px 4px rgba(0,0,0,0.18))' }}/>
            <ellipse cx="56" cy="170" rx="12" ry="18" fill="url(#ls)" opacity="0.4"/>
          </g>
          {/* RIGHT ARM */}
          <g style={{ transformOrigin:'142px 168px', animation:waveArm }}>
            <ellipse cx="144" cy="170" rx="12" ry="18" fill="#FFD600"
              style={{ filter:'drop-shadow(2px 2px 4px rgba(0,0,0,0.18))' }}/>
          </g>

          {/* Feet */}
          <ellipse cx="82"  cy="206" rx="16" ry="10" fill="#F0B000" style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.22))' }}/>
          <ellipse cx="118" cy="206" rx="16" ry="10" fill="#F0B000" style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.22))' }}/>

          {/* Stage 4+ spinning ring */}
          {stage?.level>=4 && (
            <ellipse cx="100" cy="174" rx="44" ry="14" fill="none"
              stroke="#FFD600" strokeWidth="3" strokeDasharray="8 5" opacity="0.5"
              style={{ animation:'petSpin 4s linear infinite', transformOrigin:'100px 174px' }}/>
          )}
        </g>

        {/* Zzz sleepy */}
        {mood==='sleepy' && ['Z','z','z'].map((z,i)=>(
          <text key={i} x={150+i*13} y={44-i*12} fontSize={18-i*4}
            fill="#FF9500" fontWeight="900" fontFamily="Nunito" opacity="0.9"
            style={{ animation:`petFloat ${1.8+i*0.3}s ease-in-out infinite`, animationDelay:`${i*0.5}s` }}>{z}</text>
        ))}
      </svg>
    </div>
  );
}

// ─── COSMO — Chibi Space Robot ──────────────────────────────
// Round helmet head with visor, cute antenna, LED face display
export function CosmoPet({ mood, stage, size=160, idleAnim='float', tapped, talking=false }) {
  const blink = useBlinkTimer();
  const mouthOpen = useTalkingMouth(talking);
  const happy = mood==='happy'||mood==='loved';
  const sad   = mood==='sad';
  const w = size, h = size;
  const bodyAnim = getBodyAnim(idleAnim, mood, tapped);
  const waveArm  = (mood==='happy'||idleAnim==='wave') ? 'petWaveArm 0.5s ease-in-out infinite' : 'none';

  return (
    <div style={{ position:'relative', width:w, height:h, display:'inline-block' }}>
      <div style={{ position:'absolute', inset:-16, borderRadius:'50%',
        background:'radial-gradient(circle, #00E5FF30 0%, transparent 70%)',
        animation:'petGlow 2s ease-in-out infinite', pointerEvents:'none' }}/>

      <svg viewBox="0 0 200 220" width={w} height={h} style={{ overflow:'visible' }}>
        <defs>
          <radialGradient id="ch" cx="36%" cy="25%" r="65%">
            <stop offset="0%"   stopColor="#E0FAFB"/>
            <stop offset="35%"  stopColor="#4DD0E1"/>
            <stop offset="75%"  stopColor="#0097A7"/>
            <stop offset="100%" stopColor="#006064"/>
          </radialGradient>
          <radialGradient id="cv" cx="38%" cy="30%" r="60%">
            <stop offset="0%"   stopColor="#E8FFF0"/>
            <stop offset="60%"  stopColor="#A5D6A7"/>
            <stop offset="100%" stopColor="#388E3C"/>
          </radialGradient>
          <radialGradient id="cs" cx="32%" cy="22%" r="40%">
            <stop offset="0%" stopColor="white" stopOpacity="0.5"/>
            <stop offset="100%" stopColor="white" stopOpacity="0"/>
          </radialGradient>
          <filter id="cglow">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="b"/>
            <feColorMatrix in="b" values="0 0.8 1 0 0 0 0.7 0.9 0 0 0 0 1 0 0 0 0 0 0.5 0" result="g"/>
            <feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* === ANTENNA (signature feature) === */}
        <line x1="100" y1="6" x2="100" y2="28" stroke="#006064" strokeWidth="5" strokeLinecap="round"/>
        <circle cx="100" cy="6" r="10" fill="#00E5FF"
          style={{ filter:'drop-shadow(0 0 8px #00E5FF)', animation:'petGlow 1s ease-in-out infinite' }}/>
        {/* Antenna ear bolts */}
        <circle cx="72"  cy="36" r="7" fill="#006064" style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}/>
        <circle cx="128" cy="36" r="7" fill="#006064" style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}/>

        {/* Upgrade badge stage 3+ */}
        {stage?.level>=3 && (
          <g>
            <rect x="50" y="14" width="100" height="18" rx="9" fill="#FFD600"
              style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}/>
            <text x="100" y="27" textAnchor="middle" fontSize="9" fontWeight="900" fill="#7A5000" fontFamily="Nunito">⚡ UPGRADED ⚡</text>
          </g>
        )}

        {/* === BIG ROUND HELMET HEAD === */}
        <g style={{ transformOrigin:'100px 86px', animation:bodyAnim }} filter="url(#cglow)">

          {/* Helmet outer */}
          <ellipse cx="100" cy="86" rx="74" ry="72" fill="url(#ch)"
            style={{ filter:'drop-shadow(0 6px 16px rgba(0,0,0,0.25))' }}/>
          <ellipse cx="100" cy="86" rx="74" ry="72" fill="url(#cs)"/>

          {/* Visor window — the face screen */}
          <ellipse cx="100" cy="86" rx="52" ry="50" fill="url(#cv)"
            style={{ filter:'drop-shadow(inset 0 2px 6px rgba(0,0,0,0.2))' }}/>
          <ellipse cx="100" cy="86" rx="52" ry="50" fill="none" stroke="#80DEEA" strokeWidth="3"/>
          {/* Visor shine */}
          <ellipse cx="82" cy="62" rx="16" ry="10" fill="white" opacity="0.2" transform="rotate(-20,82,62)"/>

          {/* === LED EYES on visor === */}
          <g style={{ animation: idleAnim==='look'?'petLook 1.2s ease-in-out':'none' }}>
            {blink ? (
              <>
                <line x1="66" y1="82" x2="88" y2="82" stroke="#006064" strokeWidth="6" strokeLinecap="round"/>
                <line x1="112" y1="82" x2="134" y2="82" stroke="#006064" strokeWidth="6" strokeLinecap="round"/>
              </>
            ) : (
              <>
                {/* Left LED eye */}
                <rect x="60" y="66" width="28" height="32" rx="10" fill="white"/>
                <rect x="60" y="66" width="28" height="32" rx="10" fill={happy?'#00C851':sad?'#FF2D55':'#00E5FF'} opacity="0.85"/>
                <ellipse cx="74" cy="82" rx="9" ry="10" fill={happy?'#00C851':sad?'#FF2D55':'#0097A7'}/>
                <circle cx="78" cy="76" r="4" fill="white"/>
                {/* Right LED eye */}
                <rect x="112" y="66" width="28" height="32" rx="10" fill="white"/>
                <rect x="112" y="66" width="28" height="32" rx="10" fill={happy?'#00C851':sad?'#FF2D55':'#00E5FF'} opacity="0.85"/>
                <ellipse cx="126" cy="82" rx="9" ry="10" fill={happy?'#00C851':sad?'#FF2D55':'#0097A7'}/>
                <circle cx="130" cy="76" r="4" fill="white"/>
              </>
            )}
          </g>

          {/* === LED MOUTH bar — flashes when talking === */}
          <rect x="66" y="108" width="68" height="10" rx="5" fill="#B2EBF2"/>
          <rect x="66" y="108"
            width={talking ? (mouthOpen?68:20) : happy?68:sad?14:42} height="10" rx="5"
            fill={talking ? '#00E5FF' : happy?'#00C851':sad?'#FF2D55':'#00BCD4'}
            style={{ transition:'width 0.12s' }}/>

          {/* Stage 4 circuit detail */}
          {stage?.level>=4 && (
            <>
              <line x1="28" y1="86" x2="46" y2="86" stroke="#00E5FF" strokeWidth="2.5" opacity="0.7"/>
              <line x1="154" y1="86" x2="172" y2="86" stroke="#00E5FF" strokeWidth="2.5" opacity="0.7"/>
              <circle cx="46"  cy="86" r="4" fill="#00E5FF" style={{ animation:'petGlow 1s ease-in-out infinite' }}/>
              <circle cx="154" cy="86" r="4" fill="#00E5FF" style={{ animation:'petGlow 1s ease-in-out infinite', animationDelay:'0.5s' }}/>
            </>
          )}

          {/* === CHIBI BODY === */}
          <rect x="62" y="154" width="76" height="52" rx="24" fill="#0097A7"
            style={{ filter:'drop-shadow(0 4px 10px rgba(0,0,0,0.25))' }}/>
          <rect x="62" y="154" width="76" height="52" rx="24" fill="url(#cs)"/>

          {/* LEFT ARM */}
          <g style={{ transformOrigin:'56px 168px', animation:waveArm }}>
            <rect x="42" y="154" width="20" height="40" rx="10" fill="#006064"
              style={{ filter:'drop-shadow(-2px 2px 6px rgba(0,0,0,0.3))' }}/>
          </g>
          {/* RIGHT ARM */}
          <g style={{ transformOrigin:'144px 168px', animation:waveArm }}>
            <rect x="138" y="154" width="20" height="40" rx="10" fill="#006064"
              style={{ filter:'drop-shadow(2px 2px 6px rgba(0,0,0,0.3))' }}/>
          </g>

          {/* Feet */}
          <rect x="70"  cy="204" x="70"  y="203" width="26" height="13" rx="7" fill="#004D40" style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }}/>
          <rect x="104" cy="204" x="104" y="203" width="26" height="13" rx="7" fill="#004D40" style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }}/>
        </g>

        {mood==='sleepy' && ['Z','z','z'].map((z,i)=>(
          <text key={i} x={148+i*12} y={30-i*12} fontSize={17-i*4}
            fill="#006064" fontWeight="900" fontFamily="Nunito"
            style={{ animation:`petFloat ${1.8+i*0.3}s ease-in-out infinite`, animationDelay:`${i*0.5}s` }}>{z}</text>
        ))}
      </svg>
    </div>
  );
}

// ─── NOVA — Chibi Moon Spirit ───────────────────────────────
// Round face, crescent moon horns, flowing silver wisps, star eyes
export function NovaPet({ mood, stage, size=160, idleAnim='float', tapped, talking=false }) {
  const blink = useBlinkTimer();
  const mouthOpen = useTalkingMouth(talking);
  const happy = mood==='happy'||mood==='loved';
  const sad   = mood==='sad';
  const w = size, h = size;
  const bodyAnim = getBodyAnim(idleAnim, mood, tapped);

  return (
    <div style={{ position:'relative', width:w, height:h, display:'inline-block' }}>
      <div style={{ position:'absolute', inset:-16, borderRadius:'50%',
        background:'radial-gradient(circle, #E040FB28 0%, transparent 70%)',
        animation:'petGlow 3s ease-in-out infinite', pointerEvents:'none' }}/>

      <svg viewBox="0 0 200 230" width={w} height={h} style={{ overflow:'visible' }}>
        <defs>
          <radialGradient id="nf" cx="36%" cy="24%" r="64%">
            <stop offset="0%"   stopColor="#FAF0FF"/>
            <stop offset="30%"  stopColor="#E1BEE7"/>
            <stop offset="70%"  stopColor="#9C27B0"/>
            <stop offset="100%" stopColor="#6A1B9A"/>
          </radialGradient>
          <radialGradient id="nb2" cx="36%" cy="24%" r="64%">
            <stop offset="0%"   stopColor="#F3E5F5"/>
            <stop offset="100%" stopColor="#7B1FA2"/>
          </radialGradient>
          <radialGradient id="ns" cx="32%" cy="22%" r="40%">
            <stop offset="0%" stopColor="white" stopOpacity="0.55"/>
            <stop offset="100%" stopColor="white" stopOpacity="0"/>
          </radialGradient>
          <filter id="nglow">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b"/>
            <feColorMatrix in="b" values="0.7 0 0.9 0 0 0 0.5 0.8 0 0 0 0 1 0 0 0 0 0 0.5 0" result="g"/>
            <feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Orbiting stars */}
        {stage?.level>=2 && [0,72,144,216,288].slice(0, Math.min((stage?.level||1)+1,5)).map((a,i)=>(
          <circle key={i}
            cx={100+Math.cos(a*Math.PI/180)*82} cy={90+Math.sin(a*Math.PI/180)*78}
            r={4-i*0.3} fill="#FFD600" opacity={0.9-i*0.1}
            style={{ animation:`petGlow ${1.4+i*0.3}s ease-in-out infinite`, animationDelay:`${i*0.2}s` }}/>
        ))}

        {/* === CRESCENT MOON HORNS (signature feature) === */}
        {/* Left horn */}
        <text x="44" y="42" fontSize="32" style={{ filter:'drop-shadow(0 2px 6px rgba(171,71,188,0.7))' }}>🌙</text>
        {/* Right horn — flipped */}
        <g transform="translate(156,42) scale(-1,1)">
          <text x="0" y="0" fontSize="32" style={{ filter:'drop-shadow(0 2px 6px rgba(171,71,188,0.7))' }}>🌙</text>
        </g>

        {/* Stage 3+ moon crown */}
        {stage?.level>=3 && (
          <text x="100" y="18" textAnchor="middle" fontSize="26"
            style={{ filter:'drop-shadow(0 2px 8px rgba(171,71,188,0.8))' }}>👑</text>
        )}

        {/* === BIG ROUND FACE === */}
        <g style={{ transformOrigin:'100px 90px', animation:bodyAnim }} filter="url(#nglow)">

          {/* Head shadow */}
          <ellipse cx="100" cy="94" rx="72" ry="70" fill="#6A1B9A" opacity="0.22"/>
          {/* Head */}
          <ellipse cx="100" cy="90" rx="72" ry="70" fill="url(#nf)"
            style={{ filter:'drop-shadow(0 6px 18px rgba(106,27,154,0.4))' }}/>
          <ellipse cx="100" cy="90" rx="72" ry="70" fill="url(#ns)"/>

          {/* Crescent mark on forehead (signature) */}
          <path d="M92 44 Q100 38 108 44 Q100 52 92 44 Z" fill="#6A1B9A" opacity="0.5"/>

          {/* === LARGE DREAMY EYES === */}
          <g style={{ animation: idleAnim==='look'?'petLook 1.2s ease-in-out':'none' }}>
            {blink ? (
              <>
                <path d="M60 88 Q72 80 84 88" stroke="#1A1A2E" strokeWidth="4" fill="none" strokeLinecap="round"/>
                <path d="M116 88 Q128 80 140 88" stroke="#1A1A2E" strokeWidth="4" fill="none" strokeLinecap="round"/>
              </>
            ) : (
              <>
                {/* Left eye */}
                <ellipse cx="72" cy="88" rx="18" ry="20" fill="white" style={{ filter:'drop-shadow(0 1px 4px rgba(0,0,0,0.15))' }}/>
                <ellipse cx="72" cy="90" rx="12" ry="13" fill={happy?'#1A6B1A':sad?'#4A0080':'#3A0060'}/>
                <ellipse cx="72" cy="92" rx="8"  ry="9"  fill="#0A0020"/>
                <circle  cx="78" cy="83" r="5.5" fill="white"/>
                <circle  cx="67" cy="91" r="2.5" fill="rgba(255,255,255,0.6)"/>
                {/* Star pupil when happy */}
                {happy && <text x="72" y="97" textAnchor="middle" fontSize="11" fill="#E040FB">✦</text>}
                {/* Stage 4+ galaxy shimmer */}
                {stage?.level>=4 && <circle cx="68" cy="86" r="3" fill="#E040FB" opacity="0.8" style={{ animation:'petGlow 1.2s ease-in-out infinite' }}/>}

                {/* Right eye */}
                <ellipse cx="128" cy="88" rx="18" ry="20" fill="white" style={{ filter:'drop-shadow(0 1px 4px rgba(0,0,0,0.15))' }}/>
                <ellipse cx="128" cy="90" rx="12" ry="13" fill={happy?'#1A6B1A':sad?'#4A0080':'#3A0060'}/>
                <ellipse cx="128" cy="92" rx="8"  ry="9"  fill="#0A0020"/>
                <circle  cx="134" cy="83" r="5.5" fill="white"/>
                <circle  cx="123" cy="91" r="2.5" fill="rgba(255,255,255,0.6)"/>
                {happy && <text x="128" y="97" textAnchor="middle" fontSize="11" fill="#E040FB">✦</text>}
                {stage?.level>=4 && <circle cx="124" cy="86" r="3" fill="#E040FB" opacity="0.8" style={{ animation:'petGlow 1.2s ease-in-out infinite', animationDelay:'0.3s' }}/>}
              </>
            )}
          </g>

          {/* Soft cheeks */}
          <ellipse cx="44"  cy="108" rx="16" ry="11" fill="#FF80AB" opacity={happy?0.6:0.3}/>
          <ellipse cx="156" cy="108" rx="16" ry="11" fill="#FF80AB" opacity={happy?0.6:0.3}/>

          {/* Mouth — animates when talking */}
          {talking && mouthOpen
            ? <ellipse cx="100" cy="124" rx="16" ry="9" fill="#1A1A2E"/>
            : talking && !mouthOpen
              ? <path d="M84 124 Q100 132 116 124" stroke="#1A1A2E" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              : sad
                ? <path d="M72 122 Q100 112 128 122" stroke="#1A1A2E" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
                : happy
                  ? <path d="M70 118 Q100 138 130 118" stroke="#1A1A2E" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
                  : <path d="M76 120 Q100 130 124 120" stroke="#1A1A2E" strokeWidth="3"   fill="none" strokeLinecap="round"/>
          }

          {/* === FLOWING WISP BODY === */}
          {/* Main body */}
          <path d="M100 152 C128 152 148 164 148 180 C148 196 128 214 100 218 C72 214 52 196 52 180 C52 164 72 152 100 152 Z"
            fill="url(#nb2)" style={{ filter:'drop-shadow(0 6px 14px rgba(106,27,154,0.4))' }}/>
          <path d="M100 152 C128 152 148 164 148 180 C148 196 128 214 100 218 C72 214 52 196 52 180 C52 164 72 152 100 152 Z"
            fill="url(#ns)"/>

          {/* Wispy tails */}
          <path d="M64 206 Q46 218 52 230 Q56 236 62 228 Q54 222 70 212"
            fill="none" stroke="#CE93D8" strokeWidth="10" strokeLinecap="round" opacity="0.8"
            style={{ animation:'petFloat 2.8s ease-in-out infinite' }}/>
          <path d="M136 206 Q154 218 148 230 Q144 236 138 228 Q146 222 130 212"
            fill="none" stroke="#CE93D8" strokeWidth="10" strokeLinecap="round" opacity="0.8"
            style={{ animation:'petFloat 2.8s ease-in-out infinite', animationDelay:'0.5s' }}/>
        </g>

        {mood==='sleepy' && ['Z','z','z'].map((z,i)=>(
          <text key={i} x={148+i*13} y={30-i*12} fontSize={18-i*4}
            fill="#9C27B0" fontWeight="900" fontFamily="Nunito"
            style={{ animation:`petFloat ${1.8+i*0.3}s ease-in-out infinite`, animationDelay:`${i*0.5}s` }}>{z}</text>
        ))}
      </svg>
    </div>
  );
}

// ─── UNIFIED EXPORT ─────────────────────────────────────────
export default function PetChar({ type, mood, stage, size=160, idleAnim='float', tapped=false, talking=false }) {
  if (type==='cosmo') return <CosmoPet mood={mood} stage={stage||{level:1,size:1}} size={size} idleAnim={idleAnim} tapped={tapped} talking={talking}/>;
  if (type==='nova')  return <NovaPet  mood={mood} stage={stage||{level:1,size:1}} size={size} idleAnim={idleAnim} tapped={tapped} talking={talking}/>;
  return                     <LumiePet mood={mood} stage={stage||{level:1,size:1}} size={size} idleAnim={idleAnim} tapped={tapped} talking={talking}/>;
}
